import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { TranslationContext } from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import {
  useAddParkedBasket,
  useStartNewSearch,
  useStoreDevice,
} from "@is/hooks";

type Props = {
  setIsOpen: (isOpen: boolean) => void;
  openFailedModal?: () => void;
  sessionName?: string;
};

const useManageParkBasketModal = ({
  setIsOpen,
  openFailedModal,
  sessionName,
}: Props) => {
  const { t } = useTranslation(TranslationContext.Basket);
  const [{ isPending, error }, addParkedBasket] = useAddParkedBasket();
  const { data: storeInfo } = useStoreDevice();
  const { restartSearch } = useStartNewSearch();
  const { searchTerms } = useContext(ViewStateContext).content;
  const [name, setName] = useState(sessionName ?? "");

  const fullDeviceName = useMemo(() => {
    const deviceType = storeInfo?.devices[0]?.type || "";
    const deviceName = storeInfo?.devices[0]?.name || "";

    return !!deviceName?.length && !!deviceType?.length
      ? deviceName + " / " + deviceType
      : deviceType || deviceName || "";
  }, [storeInfo]);

  const handleNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setName(event.target.value);
    },
    []
  );

  const handleSave = useCallback(async () => {
    const sessionName = name || fullDeviceName;

    const payload = {
      sessionName,
      metadata: {
        searchTerms,
      },
      deviceType: storeInfo?.devices[0]?.type,
      deviceName: storeInfo?.devices[0]?.name,
    };

    const data = await addParkedBasket(payload);
    const shouldBeAbandoned = false;

    if (data) {
      await restartSearch(shouldBeAbandoned);
    }
  }, [
    name,
    fullDeviceName,
    searchTerms,
    storeInfo,
    addParkedBasket,
    restartSearch,
  ]);

  const handleCloseModal = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  useEffect(() => {
    if (error) {
      setIsOpen(false);
      openFailedModal && openFailedModal();
    }
  }, [error, setIsOpen, openFailedModal]);

  return {
    t,
    handleSave,
    handleCloseModal,
    isPending,
    name,
    handleNameChange,
  };
};

export default useManageParkBasketModal;
